import base64 from "react-native-base64"
import { TSentence, TViseme, TWord } from "types/speechMarks"
import { Tevent } from "types/transcript"
import {
  WebsocketAudioStreamingReadyMessage,
  WebsocketBasicMessage,
  WebsocketDoneMessage,
  WebsocketErrorMessage,
  WebsocketMessageWithArgs,
  WebsocketMessageWithName,
  WebsocketMessageWithRole,
  WebsocketMessageWithStatusCode,
  WebsocketSentenceMessage,
  WebsocketSpeechMarksDoneMessage,
  WebsocketSpeechMarksMessage,
  WebsocketStartConversationMessage,
  WebsocketUserTalkingMessage,
  WebsocketWordErrorMessage,
  WebsocketWordMessage,
} from "types/websocket"

// Union type for all possible WebSocket messages
type WebsocketMessageTypes =
  | WebsocketWordMessage
  | WebsocketDoneMessage
  | WebsocketErrorMessage
  | WebsocketWordErrorMessage
  | WebsocketSpeechMarksMessage
  | WebsocketSentenceMessage
  | WebsocketMessageWithName
  | WebsocketMessageWithArgs
  | WebsocketSpeechMarksDoneMessage
  | WebsocketMessageWithRole
  | WebsocketMessageWithStatusCode

// Define a generic type T which extends the base type WebsocketMessageTypes
export class WebSocketMessage<T extends WebsocketMessageTypes> {
  rawData: string | ArrayBuffer
  parsedData: T | null

  constructor(data: string | ArrayBuffer | Blob) {
    // Normalize Blob (web) to ArrayBuffer for consistent downstream handling
    // Note: callers on web should already have ArrayBuffer due to binaryType, but we guard anyway.
    if (typeof Blob !== "undefined" && data instanceof Blob) {
      // Best effort: Blob should not occur if binaryType was set; treat as binary with no parsed JSON
      // We cannot synchronously read Blob; mark as unknown binary
      this.rawData = new ArrayBuffer(0)
      this.parsedData = null
      return
    }

    this.rawData = data as string | ArrayBuffer

    if (typeof data === "string") {
      try {
        this.parsedData = JSON.parse(data)
      } catch (error) {
        this.parsedData = null
        console.log("[WebSocketMessage] Error parsing JSON", error)
      }
    } else {
      this.parsedData = null
    }
  }

  get type(): string | undefined {
    return (this.parsedData as WebsocketBasicMessage)?.type
  }

  get skType(): string | undefined {
    return (this.parsedData as WebsocketBasicMessage)?.data?.type
  }

  get name(): string | undefined {
    return (this.parsedData as WebsocketMessageWithName)?.name
  }

  get args(): object {
    return (this.parsedData as WebsocketMessageWithArgs)?.args ?? {}
  }

  isWord(): this is { parsedData: WebsocketWordMessage } {
    return (this.parsedData as WebsocketBasicMessage)?.type === "word"
  }

  get word() {
    if (this.parsedData && (this.parsedData as WebsocketBasicMessage).type === "word") {
      return (this.parsedData as WebsocketBasicMessage).data.data
    }
    return null
  }

  get data(): any {
    return (this.parsedData as WebsocketBasicMessage)?.data
  }

  isWordDone(): this is { parsedData: WebsocketDoneMessage } {
    return this.isWord() && (this.parsedData as WebsocketBasicMessage)?.data?.status === "done"
  }

  isSentenceBufferDone(): this is { parsedData: WebsocketDoneMessage } {
    return (
      this.isSentenceBuffer && (this.parsedData as WebsocketBasicMessage)?.data?.status === "done"
    )
  }

  get isSpeechMarks(): boolean {
    return (this.parsedData as WebsocketSpeechMarksMessage)?.type === "speech_marks"
  }

  get speechMarks(): Array<TSentence | TWord | TViseme> {
    return (this.parsedData as WebsocketSpeechMarksMessage)?.data ?? []
  }

  get isSpeechMarksDone(): boolean {
    return (
      this.isSpeechMarks && (this.parsedData as WebsocketSpeechMarksDoneMessage)?.status === "done"
    )
  }

  get isSentenceBuffer(): boolean {
    return (this.parsedData as WebsocketBasicMessage)?.type === "sentenceBuffer"
  }

  get sentenceBuffer(): WebsocketSentenceMessage["data"] | undefined {
    return (this.parsedData as WebsocketSentenceMessage)?.data
  }

  get isSentence(): boolean {
    return (this.parsedData as WebsocketBasicMessage)?.type === "sentence"
  }

  get isCancelled(): boolean {
    return (this.parsedData as any)?.cancelled === true
  }

  get sentence(): string | undefined {
    return (this.parsedData as WebsocketBasicMessage)?.data
  }

  get isSentenceDone(): boolean {
    return (
      (this.parsedData as WebsocketBasicMessage)?.type === "sentence" &&
      (this.parsedData as WebsocketBasicMessage)?.data?.status === "done"
    )
  }

  get role(): string | undefined {
    return (this.parsedData as WebsocketMessageWithRole)?.role
  }

  get isEvent(): boolean {
    return (this.parsedData as WebsocketBasicMessage)?.type === "event"
  }

  get isStreamingReadyEvent(): boolean {
    return this.isEvent && this.name === "streaming_ready"
  }

  get isStreamingReady(): boolean {
    return (this.args as WebsocketAudioStreamingReadyMessage["args"]).is_ready
  }

  get isUserTalkingEvent(): boolean | null {
    return this.isEvent && this.name === "user_talking"
  }

  get isUserTalking(): boolean | null {
    return (this.args as WebsocketUserTalkingMessage["args"]).is_talking
  }

  isStartConversation(): this is { parsedData: WebsocketStartConversationMessage } {
    return (
      this.isEvent && (this.parsedData as WebsocketMessageWithName)?.name === "start_conversation"
    )
  }

  isError(): this is { parsedData: WebsocketWordErrorMessage | WebsocketErrorMessage } {
    return (
      this.isWebsocketError() ||
      (this.parsedData as WebsocketWordErrorMessage)?.data?.status === "error" ||
      false
    )
  }

  // FIXME: Discovered that the values for these error types are different than expected.
  // Only handling WebsocketErrorMessage because we've seen it recently, not sure for example if the other error
  // types like WebsocketWordErrorMessage have the same structure or not.
  // We should review these on the backend to see if we need to fix all these definitions.
  isWebsocketError(): this is { parsedData: WebsocketErrorMessage } {
    return (this.parsedData as WebsocketErrorMessage)?.type === "error" || false
  }

  get errorMessage(): string | undefined {
    return (
      (this.parsedData as WebsocketErrorMessage)?.result ||
      (this.parsedData as WebsocketBasicMessage)?.data?.result
    )
  }

  get status_code(): number | undefined {
    return (this.parsedData as WebsocketMessageWithStatusCode)?.status_code
  }

  get isAudio(): boolean {
    return this.rawData instanceof ArrayBuffer
  }

  get audioBase64(): string | undefined {
    if (this.isAudio) {
      try {
        const typedArray = new Uint8Array(this.rawData as ArrayBuffer)
        return base64.encodeFromByteArray(typedArray)
      } catch (error) {
        console.error("[WEBSOCKET] Error parsing audio buffer", error)
        return undefined
      }
    }
    return undefined
  }

  get events(): Array<Tevent> {
    return (this.parsedData as WebsocketDoneMessage)?.data?.events || []
  }

  get conversationId(): string | null {
    if (!this.isEvent || !this.parsedData) return null

    const args = (this.parsedData as WebsocketMessageWithArgs)?.args
    if (!args || typeof args !== "object") return null

    return "conversation_id" in args ? ((args.conversation_id as string | null) ?? null) : null
  }
}
